import { BoxProps, Grid, Paper, Typography } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "react-query";
import DialogFullScreen from "../../../../../../components/DialogFullScreen";
import LoadingApiQuery from "../../../../../../components/LoadingApiQuery";
import axiosClient from "../../../../../../lib/axios";
import { IDetalharPrescricao, IEditarPrescricao } from "../../../../../../types/api/receituarioAgronomico/detalhar-prescricoes";
import React, { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { enqueueSnackbar } from "notistack";
import DadosPrincipais from "./DadosPrincipais";
import InformacoesComplementares from "./InformacoesComplementares";
import CardPragas from "./CardPragas";

interface DialogDetalhePrescricaoProps {
    open: boolean;
    handleClose: () => void;
    prescricaoId: string;
}
type TDataQuery = IDetalharPrescricao | null;

export default function DialogDetalhePrescricao({
    open,
    prescricaoId,
    handleClose
}: DialogDetalhePrescricaoProps) {
    const queryClient = useQueryClient();

    const { data, isLoading, error } = useQuery({
        queryKey: [`detalhar-prescricao`, prescricaoId],
        queryFn: () => axiosClient.get(`/receituario-agronomico/prescricoes/detalhar-prescricao`, {
            params: { id: prescricaoId }
        }),
        enabled: !!prescricaoId,
        cacheTime: 0
    });

    const detalhe: TDataQuery = data?.data ?? null;
    const boxStyle: BoxProps = { component: Paper, p: 2, display: "flex", flexDirection: "column", gap: 3 };

    if (error) {
        console.error("Error fetching prescription details:", error);
    }

    const defaultFormValues: IDetalharPrescricao = detalhe ? {
        ...detalhe,
        agrotoxico_id: detalhe.agrotoxico?.id ?? 0,
        cultura_id: detalhe.cultura?.id ?? 0,
    } : {} as IDetalharPrescricao;

    const methods = useForm<IDetalharPrescricao>({
        defaultValues: defaultFormValues
    });

    useEffect(() => {
        if (detalhe) {
            methods.reset({
                agrotoxico_id: detalhe.agrotoxico?.id,
                agrotoxico: detalhe.agrotoxico,
                cultura: detalhe.cultura,
                cultura_id: detalhe.cultura?.id,
                formulacao: detalhe.formulacao ?? "",
                concentracao: detalhe.concentracao ?? "",
                quantidade_dose: detalhe.quantidade_dose,
                unidade_dose: detalhe.unidade_dose,
                tamanho_area: detalhe.tamanho_area,
                unidade_area: detalhe.unidade_area,
                quantidade_aplicacoes: detalhe.quantidade_aplicacoes,
                dias_intervalo_aplicacoes: detalhe.dias_intervalo_aplicacoes,
                dias_carencia: detalhe.dias_carencia,
                complemento: detalhe.complemento ?? "",
                modo_aplicacao: detalhe.modo_aplicacao ?? "",
                manejo: detalhe.manejo ?? "",
                epoca_aplicacao: detalhe.epoca_aplicacao ?? "",
                grupo_quimico: detalhe.grupo_quimico ?? "",
            });
        }
    }, [detalhe, methods]);
    const { mutate: editarPrescricao, isLoading: isEditing } = useMutation({
        mutationKey: ["editar-prescricao", prescricaoId],
        mutationFn: (data: IEditarPrescricao) =>
            axiosClient.put(`/receituario-agronomico/prescricoes/editar-prescricao/${prescricaoId}`, data),
        onError: (error: any) => {
            enqueueSnackbar(error?.response?.data?.message ?? "Erro ao editar prescrição!", { variant: "error" });
        },
        onSuccess: () => {
            queryClient.invalidateQueries("listar-todas-prescricoes");
            queryClient.invalidateQueries(["detalhar-prescricao", prescricaoId]);
            enqueueSnackbar("Prescrição editada com sucesso!", {
                variant: "success",
            });
        },
    });

    const handleSubmit = () => {
        const data = methods.getValues();
        if (!data.agrotoxico?.id || !data.cultura?.id) {
            enqueueSnackbar("Preencha Agrotóxico e Cultura!", { variant: "error" });
            return;
        }
        editarPrescricao({
            ...data,
            agrotoxico_id: data.agrotoxico?.id,
            cultura_id: data.cultura?.id,
        });
    }

    return (
        <DialogFullScreen
            {...{ open, handleClose }}
            title="Detalhe da Prescrição"
        >
            {isLoading && <LoadingApiQuery />}
            {
                (!isLoading && detalhe) &&
                <FormProvider {...methods}>
                    <Grid container spacing={3}>
                        <Grid size={{xs: 12, md: 4}}>
                            <Grid container spacing={3}>
                                <Grid size={{xs: 12}}>
                                    <DadosPrincipais
                                        boxStyle={boxStyle}
                                        detalhe={detalhe}
                                        onSubmitStep={handleSubmit}
                                        isEditing={isEditing}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>

                        <Grid size={{xs: 12, md: 8}}>
                            <Grid container spacing={3}>
                                <Grid size={{xs: 12}}>
                                    <CardPragas detalhe={detalhe} />
                                </Grid>
                                <Grid size={{xs: 12}}>
                                    <InformacoesComplementares
                                        boxStyle={boxStyle}
                                        detalhe={detalhe}
                                        onSubmitStep={handleSubmit}
                                        isEditing={isEditing}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </FormProvider>
            }
            {(!isLoading && !detalhe && !error) && (
                <Typography sx={{ p: 2 }}>Não foi possível carregar os detalhes da prescrição.</Typography>
            )}
        </DialogFullScreen>
    );
}